/**
 * SkillSphere — sphère 3D interactive de compétences
 *
 * Les icônes des compétences sont réparties sur une sphère (distribution de Fibonacci),
 * qui tourne automatiquement et que l'on peut faire pivoter à la souris ou au doigt.
 * Rendu performant : les transformations 3D sont appliquées directement au DOM via refs
 * dans une boucle requestAnimationFrame (pas de re-render React par frame).
 * Respecte prefers-reduced-motion et fournit une liste accessible (sr-only).
 */
import React, { useEffect, useMemo, useRef } from 'react';
import { TechIcon } from '../../../shared/components/TechIcon/TechIcon';
import * as styles from './skills.css';

interface SphereSkill {
  id: string;
  name_fr: string;
  name_en?: string;
  icon_type?: string;
  image_url?: string;
  color?: string;
  haloColor?: string;
}

interface SkillSphereProps {
  skills: SphereSkill[];
  language?: string;
  rotationSpeed?: number;
  size?: number;
  iconSize?: number;
  showNames?: boolean;
  categoryHalo?: boolean;
}

export const SkillSphere: React.FC<SkillSphereProps> = ({
  skills,
  language = 'fr',
  rotationSpeed = 1,
  size = 460,
  iconSize = 28,
  showNames = true,
  categoryHalo = false,
}) => {
  const stageRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const rotation = useRef({ x: 0.35, y: 0 });
  const velocity = useRef({ x: 0, y: 0.0035 });
  const dragging = useRef(false);
  const last = useRef({ x: 0, y: 0 });

  // Positions réparties uniformément sur la sphère (spirale de Fibonacci)
  const positions = useMemo(() => {
    const n = Math.max(skills.length, 1);
    const golden = Math.PI * (1 + Math.sqrt(5));
    return skills.map((_, i) => {
      const phi = Math.acos(1 - (2 * (i + 0.5)) / n);
      const theta = golden * (i + 0.5);
      return {
        x: Math.sin(phi) * Math.cos(theta),
        y: Math.sin(phi) * Math.sin(theta),
        z: Math.cos(phi),
      };
    });
  }, [skills]);

  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const baseSpin = reduced ? 0 : 0.0035 * rotationSpeed;
    if (reduced) velocity.current = { x: 0, y: 0 };

    let raf = 0;
    const animate = () => {
      const stage = stageRef.current;
      if (stage) {
        const size = stage.clientWidth;
        const R = size * 0.36;
        const rot = rotation.current;

        if (!dragging.current) {
          rot.x += velocity.current.x;
          rot.y += velocity.current.y;
          // Inertie : on amortit doucement vers la rotation automatique de base
          velocity.current.x *= 0.94;
          velocity.current.y += (baseSpin - velocity.current.y) * 0.03;
        }

        const sinX = Math.sin(rot.x), cosX = Math.cos(rot.x);
        const sinY = Math.sin(rot.y), cosY = Math.cos(rot.y);

        for (let i = 0; i < positions.length; i++) {
          const p = positions[i];
          // Rotation autour de Y puis de X
          const x1 = p.x * cosY - p.z * sinY;
          const z1 = p.x * sinY + p.z * cosY;
          const y1 = p.y * cosX - z1 * sinX;
          const z2 = p.y * sinX + z1 * cosX;

          const scale = (z2 + 1.8) / 2.8; // ~0.28 (arrière) .. 1 (avant)
          const el = itemRefs.current[i];
          if (el) {
            el.style.transform =
              `translate(-50%, -50%) translate3d(${(x1 * R).toFixed(1)}px, ${(y1 * R).toFixed(1)}px, 0) scale(${scale.toFixed(3)})`;
            el.style.opacity = Math.max(0.28, (z2 + 1.3) / 2.3).toFixed(3);
            el.style.zIndex = String(Math.round(scale * 100));
          }
        }
      }
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [positions, rotationSpeed]);

  const onPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    dragging.current = true;
    last.current = { x: e.clientX, y: e.clientY };
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* noop */ }
  };

  const onPointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragging.current) return;
    const dx = e.clientX - last.current.x;
    const dy = e.clientY - last.current.y;
    last.current = { x: e.clientX, y: e.clientY };
    rotation.current.y += dx * 0.006;
    rotation.current.x -= dy * 0.006;
    velocity.current = { x: -dy * 0.0006, y: dx * 0.0006 };
  };

  const onPointerUp = () => { dragging.current = false; };

  const labelOf = (s: SphereSkill) => (language === 'en' ? (s.name_en || s.name_fr) : s.name_fr);

  if (skills.length === 0) return null;

  return (
    <div className={styles.sphereWrapper}>
      <div
        ref={stageRef}
        className={styles.sphereStage}
        style={{ width: `min(86vw, ${size}px)`, height: `min(86vw, ${size}px)` }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        role="group"
        aria-label={language === 'en' ? 'Interactive skills sphere' : 'Sphère interactive de compétences'}
      >
        {skills.map((s, i) => (
          <div
            key={s.id}
            ref={(el) => { itemRefs.current[i] = el; }}
            className={styles.sphereItem}
            title={labelOf(s)}
          >
            <span
              style={categoryHalo ? {
                display: 'inline-flex',
                padding: '6px',
                borderRadius: '50%',
                background: `radial-gradient(circle, ${s.haloColor || s.color || '#64ffda'}33 0%, transparent 70%)`,
                boxShadow: `0 0 10px ${s.haloColor || s.color || '#64ffda'}66`,
              } : { display: 'inline-flex' }}
            >
              <TechIcon type={s.image_url || s.icon_type || 'code'} size={iconSize} color={s.color} />
            </span>
            {showNames && <span className={styles.sphereName}>{labelOf(s)}</span>}
          </div>
        ))}
      </div>
      {/* Liste accessible (lecteurs d'écran / SEO) */}
      <ul className={styles.srOnly}>
        {skills.map((s) => (
          <li key={s.id}>{labelOf(s)}</li>
        ))}
      </ul>
    </div>
  );
};

export default SkillSphere;
